import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

/**
 * "My Documents" — the marketer's home base. Lists every design request
 * the current user created, with a small status pill, a primary action
 * (open in the workflow stepper, or download PDF), and a disabled
 * print-order button (fulfilment partner TBD).
 */
export default function MyDocuments() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/my-documents`);
      setRows(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not load");
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="my-documents-root" className="max-w-5xl">
      <p className="text-xs uppercase tracking-[0.22em] text-[#6B6358]">Marketer home</p>
      <h1 className="font-serif text-4xl tracking-tight mt-1">My Projects</h1>
      <p className="text-sm text-[#3D3A35] mt-3 max-w-2xl">
        Every project you've started. Open one to continue the
        Inputs &rarr; Content &rarr; Design workflow, or download a finished PDF.
      </p>

      {error && <p className="text-sm text-[#B5462F] mt-4">{error}</p>}

      {rows == null ? (
        <p className="text-[#6B6358] mt-6">Loading&hellip;</p>
      ) : rows.length === 0 ? (
        <div
          data-testid="my-documents-empty"
          className="mt-8 border border-dashed border-[#1A1F2C]/20 rounded-lg p-10 text-center"
        >
          <p className="text-[#6B6358]">
            You haven't started any design requests yet.
          </p>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-3">
          {rows.map((r) => (
            <DocumentRow key={r.design_request_id} row={r} onChanged={load} />
          ))}
        </div>
      )}
    </div>
  );
}

function DocumentRow({ row, onChanged }) {
  const pillStyle = pillForRow(row);
  const dt = row.updated_at ? new Date(row.updated_at).toLocaleString() : "—";
  const fallbackTitle = row.promotion || row.campaign || "Untitled draft";
  const title = row.name || fallbackTitle;

  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState(row.name || "");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState("");

  const rename = async () => {
    setError("");
    const next = nameDraft.trim();
    if (next === (row.name || "")) { setRenaming(false); return; }
    try {
      await api.patch(`/brands/${row.brand_id}/design-requests/${row.design_request_id}`,
        { name: next || null });
      setRenaming(false);
      onChanged?.();
    } catch (e) {
      setError(e.response?.data?.detail || "Rename failed");
    }
  };

  const remove = async () => {
    setError("");
    try {
      await api.delete(`/brands/${row.brand_id}/design-requests/${row.design_request_id}`);
      setConfirmDelete(false);
      onChanged?.();
    } catch (e) {
      setError(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div
      data-testid={`my-document-row-${row.design_request_id}`}
      className="border border-[#1A1F2C]/10 bg-white rounded-lg p-5"
    >
      <div className="flex flex-wrap gap-4 items-baseline justify-between">
        <div className="flex-1 min-w-[300px]">
          <div className="flex flex-wrap gap-2 items-center">
            <span
              className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full"
              style={{ backgroundColor: `${pillStyle.color}1a`, color: pillStyle.color }}
            >
              {pillStyle.label}
            </span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6358]">
              {row.brand_name}
            </span>
          </div>
          {!renaming ? (
            <h3
              data-testid={`my-document-row-${row.design_request_id}-title`}
              className="text-base font-medium text-[#1A1F2C] mt-1 cursor-text hover:underline decoration-dotted"
              title="Click to rename"
              onClick={() => { setNameDraft(row.name || ""); setRenaming(true); }}
            >
              {title}
              {row.event_type && (
                <span className="text-sm font-normal text-[#6B6358]"> · {row.event_type}</span>
              )}
            </h3>
          ) : (
            <div className="flex gap-2 items-center mt-1">
              <input
                autoFocus
                data-testid={`my-document-row-${row.design_request_id}-rename-input`}
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                placeholder={fallbackTitle}
                onKeyDown={(e) => {
                  if (e.key === "Enter") rename();
                  if (e.key === "Escape") { setRenaming(false); setNameDraft(row.name || ""); }
                }}
                className="flex-1 max-w-md px-3 py-1.5 border border-[#1A1F2C]/30 rounded bg-[#F8F5EE] text-sm"
              />
              <button
                data-testid={`my-document-row-${row.design_request_id}-rename-save`}
                onClick={rename}
                className="px-3 py-1.5 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-xs uppercase tracking-[0.18em]"
              >Save</button>
              <button
                onClick={() => { setRenaming(false); setNameDraft(row.name || ""); }}
                className="text-xs text-[#6B6358]"
              >Cancel</button>
            </div>
          )}
          <p className="text-xs text-[#6B6358] mt-1">
            Updated {dt} &middot; {row.design_request_id}
          </p>
          {error && <p className="text-xs text-[#B5462F] mt-2">{error}</p>}
        </div>
        <div className="flex gap-2 items-center">
          {row.exported_pdf_url ? (
            <DownloadFormats row={row} />
          ) : null}
          <Link
            to={`/brands/${row.brand_id}/design-requests/${row.design_request_id}`}
            data-testid={`my-document-row-${row.design_request_id}-open`}
            className="px-3 py-1.5 text-xs uppercase tracking-[0.18em] border border-[#1A1F2C]/30 rounded-full text-[#3D3A35]"
          >
            Open
          </Link>
          <button
            type="button"
            disabled
            data-testid={`my-document-row-${row.design_request_id}-print`}
            title="Coming soon — fulfilment partner TBD"
            className="px-3 py-1.5 text-xs uppercase tracking-[0.18em] border border-[#1A1F2C]/15 rounded-full text-[#6B6358] cursor-not-allowed opacity-60"
          >
            Print order
          </button>
          <button
            type="button"
            data-testid={`my-document-row-${row.design_request_id}-delete`}
            onClick={() => setConfirmDelete(true)}
            className="px-3 py-1.5 text-xs uppercase tracking-[0.18em] border border-[#B5462F]/40 text-[#B5462F] rounded-full"
          >
            Delete
          </button>
        </div>
      </div>

      {confirmDelete && (
        <DeleteConfirm
          title={`Delete "${title}"?`}
          message="The design request, generated document version, and any blockers attached to it will be permanently removed. Exported PDFs already produced will remain on Pica9 PDFMaker."
          onConfirm={remove}
          onCancel={() => setConfirmDelete(false)}
          testid={`my-document-row-${row.design_request_id}-delete-confirm`}
        />
      )}
    </div>
  );
}

function DownloadFormats({ row }) {
  const [formats, setFormats] = useState(null);
  useEffect(() => {
    if (!row.document_version_id) return;
    api.get(`/document-versions/${row.document_version_id}/allowed-formats`)
      .then(({ data }) => setFormats(data.formats || []))
      .catch(() => setFormats([{ id: "print_cmyk_pdf", label: "Download PDF", renderable: true }]));
  }, [row.document_version_id]);
  if (!formats) return null;
  return (
    <div
      data-testid={`my-document-row-${row.design_request_id}-formats`}
      className="flex gap-1.5 items-center flex-wrap"
    >
      {formats.map((f) =>
        f.renderable ? (
          <a
            key={f.id}
            href={row.exported_pdf_url}
            target="_blank"
            rel="noreferrer"
            data-testid={`my-document-row-${row.design_request_id}-download-${f.id}`}
            className="px-3 py-1.5 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-xs uppercase tracking-[0.16em]"
          >
            {f.label}
          </a>
        ) : (
          <button
            key={f.id}
            type="button"
            disabled
            title="Coming soon — derived from the CMYK PDF by the rendering engine"
            data-testid={`my-document-row-${row.design_request_id}-download-${f.id}`}
            className="px-3 py-1.5 border border-[#1A1F2C]/15 rounded-full text-xs uppercase tracking-[0.16em] text-[#6B6358] opacity-60 cursor-not-allowed"
          >
            {f.label} · soon
          </button>
        )
      )}
    </div>
  );
}


function DeleteConfirm({ title, message, onConfirm, onCancel, testid }) {
  return (
    <div
      data-testid={testid}
      className="fixed inset-0 bg-[#1A1F2C]/60 z-50 flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div className="bg-[#F8F5EE] rounded-lg max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-serif text-2xl tracking-tight">{title}</h3>
        <p className="text-sm text-[#3D3A35] mt-2">{message}</p>
        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-xs uppercase tracking-[0.18em] text-[#6B6358]"
          >Cancel</button>
          <button
            data-testid={`${testid}-confirm`}
            onClick={onConfirm}
            className="px-4 py-2 bg-[#B5462F] text-[#F8F5EE] rounded-full text-xs uppercase tracking-[0.18em]"
          >Delete</button>
        </div>
      </div>
    </div>
  );
}

function pillForRow(row) {
  if (row.lifecycle_substate === "paused_for_brand") {
    return { color: "#C28A2E", label: "Paused" };
  }
  if (row.export_status === "failed") {
    return { color: "#B5462F", label: "PDF failed" };
  }
  if (row.approval_status === "approved" && row.exported_pdf_url) {
    return { color: "#1F6B6B", label: row.approval_was_auto ? "Finished" : "Approved" };
  }
  if (row.approval_status === "approved") return { color: "#1F6B6B", label: "Approved" };
  if (row.approval_status === "submitted") return { color: "#5B5EBE", label: "In review" };
  if (row.approval_status === "rejected") return { color: "#B5462F", label: "Rejected" };
  if (row.status === "ready") return { color: "#3D3A35", label: "Ready" };
  if (row.status === "generating") return { color: "#5B5EBE", label: "Generating" };
  if (row.status === "failed") return { color: "#B5462F", label: "Failed" };
  return { color: "#6B6358", label: "Draft" };
}
